import React from 'react';
import ReactDOM from 'react-dom';
import QuoteRow from '../../pages/QuoteRow';
import InvoiceModal from '../../pages/InvoiceModal';
import InvoicePDFPreviewModal from '../../pages/InvoicePDFPreviewModal';
import { useState, useEffect, useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { buildInvoicePdfHtmlDocument, waitForImagesInElement } from '../../utils/invoicePdfHtml';
import { stampDevelloFooterOnAllPages } from '../../utils/invoicePdfStamp';
import { supabase } from '../../supabase';
import {
  runSupabaseAuthedOperation,
  withTimeout,
  devReliabilityLog,
  safeRemoveDetachedNode,
} from '../../utils/asyncReliability';
import {
  DashboardPageHeader,
  dashboardCardClass,
  dashboardCardHeaderClass,
  dashboardInputClass,
  dashboardSelectClass,
  dashboardBtnPrimary,
  dashboardBtnModalCancel,
  dashboardBtnModalDanger,
  dashboardModalBackdrop,
  dashboardModalWrap,
  dashboardModalPanelMd,
  dashboardModalHeader,
  dashboardModalBody,
  dashboardModalFooter,
} from './DashboardChrome';

const QuotesSection = ({
  quotes,
  setQuotes,
  clients,
  filteredQuotes,
  quoteSearchTerm,
  setQuoteSearchTerm,
  quoteStatusFilter,
  setQuoteStatusFilter,
  showCreateQuote,
  setShowCreateQuote,
  showEditQuote,
  setShowEditQuote,
  quoteForm,
  setQuoteForm,
  saveQuote,
  deleteQuote,
  editQuote,
  calculateQuoteTotals,
  dropdownOpenId,
  setDropdownOpenId,
  handleSendEmail,
  onInvoiceSentStatusClick,
  onInvoiceAmountUpdate,
  onToggleInvoicePayment,
  quoteRowMutationBusyId = null,
  forceDropdownScrollable,
  setIsEditingQuote,
  setShowReviewQuote,
  setIsInReviewMode,
  highlightedQuoteId,
  setHighlightedQuoteId,
  plan = 'free',
  ...rest
}) => {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  // Add any additional logic or destructure more props as needed
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceModalData, setInvoiceModalData] = useState(null);
  const [showInvoicePDFPreview, setShowInvoicePDFPreview] = useState(false);
  const [invoicePDFUrl, setInvoicePDFUrl] = useState(null);
  const highlightedQuoteRef = useRef(null);

  /** Close quote create/edit/review modals so the invoice modal stacks cleanly above. */
  const closeQuoteShellModals = () => {
    setShowCreateQuote(false);
    setShowEditQuote(false);
    setShowReviewQuote(false);
    setIsInReviewMode(false);
    setIsEditingQuote(false);
  };

  // Handle quote highlighting and scrolling
  useEffect(() => {
    if (highlightedQuoteId && highlightedQuoteRef.current) {
      // Scroll to the highlighted quote
      highlightedQuoteRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      
      // Clear the highlight after a delay
      setTimeout(() => {
        setHighlightedQuoteId(null);
      }, 3000);
    }
  }, [highlightedQuoteId, setHighlightedQuoteId]);

  // Pagination logic
  const totalPages = Math.ceil(filteredQuotes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentQuotes = filteredQuotes.slice(startIndex, endIndex);

  // Reset to first page when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [quoteSearchTerm, quoteStatusFilter]);

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Delete confirmation handlers
  const handleDeleteClick = (quote) => {
    setQuoteToDelete(quote);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (quoteToDelete) {
      deleteQuote(quoteToDelete.id);
      setShowDeleteModal(false);
      setQuoteToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setQuoteToDelete(null);
  };

  /** PostgreSQL/Supabase: strip `undefined` from nested objects before sending. */
  const omitUndefinedDeep = (value) => {
    if (value === undefined) return undefined;
    if (value === null) return null;
    if (value instanceof Date) return value;
    if (Array.isArray(value)) {
      return value
        .map((item) => omitUndefinedDeep(item))
        .filter((item) => item !== undefined);
    }
    if (typeof value === 'object') {
      const out = {};
      for (const [k, v] of Object.entries(value)) {
        if (v === undefined) continue;
        const next = omitUndefinedDeep(v);
        if (next !== undefined) out[k] = next;
      }
      return out;
    }
    return value;
  };

  // Save invoice data to quote
  const handleSaveInvoiceData = async (quoteId, invoiceData, pdfUrl) => {
    devReliabilityLog('saveInvoiceData:start', { quoteId, hasPdfUrl: !!pdfUrl });
    if (!supabase) {
      const err = new Error('CONFIG');
      /** @type {{ userFacing?: string }} */ (err).userFacing =
        'App configuration error. Please refresh the page and try again.';
      throw err;
    }
    const cleanedInvoiceData = omitUndefinedDeep(invoiceData);
    const updateData = {
      invoice_data: cleanedInvoiceData,
      invoice_generated_at: new Date().toISOString(),
    };

    const result = await runSupabaseAuthedOperation({
      name: 'saveInvoiceData',
      supabaseClient: supabase,
      timeoutMs: 50000,
      retries: 1,
      fn: async () => {
        const { error } = await supabase.from('quotes').update(updateData).eq('id', quoteId);
        if (error) throw error;
      },
    });

    if (!result.ok) {
      const err = new Error(result.userMessage);
      /** @type {{ userFacing?: string }} */ (err).userFacing = result.userMessage;
      throw err;
    }

    const at = new Date().toISOString();
    setQuotes((prev) =>
      prev.map((quote) =>
        quote.id === quoteId ? { ...quote, invoiceData: cleanedInvoiceData, invoiceGeneratedAt: at } : quote
      )
    );
  };

  // Edit invoice
  const handleEditInvoice = (quote) => {
    closeQuoteShellModals();
    setInvoiceModalData({
      quote,
      client: clients.find((c) => c.id === quote.clientId),
      isEditing: true,
      existingInvoiceData: quote.invoiceData,
    });
    setShowInvoiceModal(true);
  };

  // PDF Generation for Invoice (bounded time, DOM cleanup on failure — avoids stuck canvas / orphan nodes)
  const handleGenerateInvoicePDF = async (invoiceData) => {
    let pdfContent = null;
    try {
      devReliabilityLog('invoicePdf:start', {});

      const pdfUrl = await withTimeout(
        (async () => {
          pdfContent = document.createElement('div');
          pdfContent.style.width = '760px';
          pdfContent.style.padding = '0';
          pdfContent.style.margin = '0';
          pdfContent.style.backgroundColor = '#ffffff';
          pdfContent.style.position = 'absolute';
          pdfContent.style.left = '-9999px';
          pdfContent.style.top = '0';

          pdfContent.innerHTML = await buildInvoicePdfHtmlDocument(invoiceData);
          document.body.appendChild(pdfContent);
          await waitForImagesInElement(pdfContent);
          await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

          const canvas = await html2canvas(pdfContent, {
            scale: 2.5,
            useCORS: true,
            allowTaint: false,
            backgroundColor: '#ffffff',
            logging: false,
          });

          const imgData = canvas.toDataURL('image/png');
          const pdf = new jsPDF('p', 'mm', 'a4');
          const imgWidth = 210;
          const pageHeight = 295;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          let heightLeft = imgHeight;
          let position = 0;

          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;

          while (heightLeft >= 0) {
            position = heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
          }

          if (plan !== 'pro') {
            await stampDevelloFooterOnAllPages(pdf);
          }

          const pdfBlob = pdf.output('blob');
          return URL.createObjectURL(pdfBlob);
        })(),
        180000,
        'invoicePdfPipeline'
      );

      setInvoicePDFUrl(pdfUrl);
      setShowInvoicePDFPreview(true);
      devReliabilityLog('invoicePdf:done', {});
      return pdfUrl;
    } catch (error) {
      devReliabilityLog('invoicePdf:fail', { err: String(error) });
      throw error;
    } finally {
      safeRemoveDetachedNode(pdfContent);
    }
  };

  return (
    <div className="invoices-section max-w-7xl mx-auto space-y-8 pb-10">
      <DashboardPageHeader
        eyebrow="Dashboard"
        title="Invoices"
        description="Create quotes, send invoices, and track payment status in one list."
        action={
          <button
            type="button"
            onClick={() => {
              setIsEditingQuote(false);
              if (setShowReviewQuote) setShowReviewQuote(false);
              if (setIsInReviewMode) setIsInReviewMode(false);
              setShowEditQuote(false);
              setQuoteForm({
                clientId: '',
                materials: [],
                markupType: 'percentage',
                taxEnabled: false,
                finalTaxEnabled: false,
                taxRate: '',
                labour: {
                  entries: [],
                  total: 0,
                },
                description: '',
                editedOn: new Date().toISOString(),
                quoteNumber: '',
                customQuoteData: {},
              });
              setShowCreateQuote(true);
            }}
            className={dashboardBtnPrimary}
          >
            Create new invoice
          </button>
        }
      />

      <div className={dashboardCardClass}>
        <div className={`${dashboardCardHeaderClass} flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between`}>
          <h3 className="text-base font-semibold text-slate-900">All invoices</h3>
          <div className="flex flex-col gap-2 sm:flex-row sm:space-x-2 sm:space-y-0">
            <input
              type="text"
              placeholder="Search by client name…"
              value={quoteSearchTerm}
              onChange={(e) => setQuoteSearchTerm(e.target.value)}
              className={dashboardInputClass}
            />
            <select
              value={quoteStatusFilter}
              onChange={(e) => setQuoteStatusFilter(e.target.value)}
              className={`${dashboardSelectClass} min-w-[180px]`}
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="sent_invoice">Sent Invoice</option>
              <option value="payment_awaiting">Awaiting payment</option>
              <option value="payment_paid">Payment complete</option>
            </select>
          </div>
        </div>
        <div className="p-6">
          {filteredQuotes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>{quotes.length === 0 ? 'No invoices yet' : 'No invoices match your search'}</p>
              <p className="text-sm">
                {quotes.length === 0 ? 'Create your first invoice' : 'Try adjusting your search or filters'}
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {currentQuotes.map((quote) => {
                const client = clients.find(c => c.id === quote.clientId);
                const totals = calculateQuoteTotals(quote);
                const quoteTotal = totals.total;
                const isHighlighted = quote.id === highlightedQuoteId;
                return (
                  <div
                    key={quote.id}
                    ref={isHighlighted ? highlightedQuoteRef : null}
                    className={`transition-all duration-500 ${
                      isHighlighted 
                        ? 'ring-4 ring-primary ring-opacity-50 bg-primary/5 scale-[1.02]' 
                        : ''
                    }`}
                  >
                    <QuoteRow
                      quote={quote}
                      client={client}
                      quoteTotal={quoteTotal}
                      editQuote={editQuote}
                      deleteQuote={() => handleDeleteClick(quote)}
                      handleSendEmail={handleSendEmail}
                      canSendFromDevello={plan === 'pro'}
                      onInvoiceSentStatusClick={onInvoiceSentStatusClick}
                      onInvoiceAmountUpdate={onInvoiceAmountUpdate}
                      onToggleInvoicePayment={onToggleInvoicePayment}
                      quoteRowMutationBusyId={quoteRowMutationBusyId}
                      dropdownOpenId={dropdownOpenId}
                      setDropdownOpenId={setDropdownOpenId}
                      forceDropdownScrollable={forceDropdownScrollable}
                      onCreateInvoice={() => {
                        closeQuoteShellModals();
                        setDropdownOpenId(null);
                        setInvoiceModalData({ quote, client });
                        setShowInvoiceModal(true);
                      }}
                      onEditInvoice={() => handleEditInvoice(quote)}
                    />
                  </div>
                );
              })}
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredQuotes.length)} of {filteredQuotes.length} invoices
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={goToPreviousPage}
                      disabled={currentPage === 1}
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Previous
                    </button>

                    <div className="flex items-center space-x-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => goToPage(page)}
                          className={`rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
                            currentPage === page
                              ? 'bg-primary text-white shadow-md shadow-primary/20'
                              : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages}
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* Modals and other UI can be added here using ...modalsAndOtherProps */}
      <InvoiceModal
        key={`${showInvoiceModal ? 'open' : 'closed'}-${invoiceModalData?.quote?.id || 'new'}-${invoiceModalData?.isEditing ? 'edit' : 'create'}`}
        showInvoiceModal={showInvoiceModal}
        setShowInvoiceModal={setShowInvoiceModal}
        initialInvoiceData={invoiceModalData}
        onGeneratePDF={handleGenerateInvoicePDF}
        onSaveInvoiceData={handleSaveInvoiceData}
        settings={rest.settings} // <-- pass settings prop
        plan={plan}
      />
      <InvoicePDFPreviewModal
        key={`${showInvoicePDFPreview ? 'open' : 'closed'}-${invoicePDFUrl || 'none'}`}
        isOpen={showInvoicePDFPreview}
        onClose={() => setShowInvoicePDFPreview(false)}
        pdfUrl={invoicePDFUrl}
      />
      
      {showDeleteModal &&
        ReactDOM.createPortal(
          <>
            <div className={dashboardModalBackdrop} aria-hidden />
            <div className={dashboardModalWrap}>
              <div className={dashboardModalPanelMd} role="dialog" aria-modal="true" aria-labelledby="delete-invoice-title">
                <div className={dashboardModalHeader}>
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-100">
                      <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Invoices</p>
                      <h3 id="delete-invoice-title" className="text-lg font-semibold text-slate-900">
                        Delete invoice
                      </h3>
                    </div>
                  </div>
                </div>
                <div className={dashboardModalBody}>
                  <p className="text-sm text-slate-600">
                    Are you sure you want to delete the invoice for{' '}
                    <span className="font-semibold text-slate-900">{quoteToDelete?.clientName || 'this client'}</span>?
                  </p>
                  <p className="mt-2 text-xs leading-relaxed text-slate-500">
                    This cannot be undone. PDFs and invoice data tied to this record will be removed.
                  </p>
                </div>
                <div className={dashboardModalFooter}>
                  <button type="button" onClick={handleCancelDelete} className={dashboardBtnModalCancel}>
                    Cancel
                  </button>
                  <button type="button" onClick={handleConfirmDelete} className={dashboardBtnModalDanger}>
                    Delete invoice
                  </button>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
};

export default QuotesSection; 